// Analytics utility functions
import { User } from 'firebase/auth';

// GA4 Measurement ID and Google Ads ID
export const GA_MEASUREMENT_ID = 'G-JLV8V0WR0K';
export const GOOGLE_ADS_ID = 'AW-1018882741';

// Event categories
export enum EventCategory {
  Auth = 'auth',
  Server = 'server',
  Subscription = 'subscription',
  UI = 'ui',
  Error = 'error',
  Performance = 'performance',
}

// Define common event names for consistency
export enum EventName {
  // Auth events
  SignIn = 'sign_in',
  SignOut = 'sign_out',
  SignUp = 'sign_up',
  PasswordReset = 'password_reset',
  
  // Server events
  ServerCreate = 'server_create',
  ServerStart = 'server_start',
  ServerStop = 'server_stop',
  ServerDelete = 'server_delete',
  ServerDeploy = 'server_deploy',
  ServerConfig = 'server_configure',
  
  // Subscription events
  SubscriptionView = 'subscription_view',
  SubscriptionPurchase = 'subscription_purchase',
  SubscriptionCancel = 'subscription_cancel',
  SubscriptionUpgrade = 'subscription_upgrade',
  
  // UI events
  ButtonClick = 'button_click',
  PageView = 'page_view',
  ModalOpen = 'modal_open',
  ModalClose = 'modal_close',
  FeatureUse = 'feature_use',
  
  // Error events
  ApiError = 'api_error',
  ClientError = 'client_error',
  ValidationError = 'validation_error',
  
  // Performance events
  PageLoad = 'page_load',
  ApiCall = 'api_call',
  ComponentRender = 'component_render',
}

// Track a standard event
export function trackEvent(
  eventName: EventName | string,
  eventParams?: Record<string, any>,
  nonInteraction = false
) {
  try {
    if (typeof window === 'undefined') return;
    
    // Check if gtag is available and consent is granted
    if (!window.gtag) {
      // Queue the event if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['event', eventName, {
        ...eventParams,
        non_interaction: nonInteraction,
      }]);
      
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[Analytics] Event queued (gtag not ready): ${eventName}`, eventParams);
      }
      return;
    }
    
    // Log event to Google Analytics
    window.gtag('event', eventName, {
      ...eventParams,
      non_interaction: nonInteraction,
    });
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Analytics] Event: ${eventName}`, eventParams);
    }
  } catch (error) {
    // Don't let analytics errors break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error tracking event:', error);
    }
  }
}

// Track exceptions/errors
export function trackError(
  error: Error | string,
  context?: Record<string, any>,
  fatal = false
) {
  try {
    if (typeof window === 'undefined') return;
    
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'string' ? undefined : error.stack;
    
    if (!window.gtag) {
      // Queue the error if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['event', 'exception', {
        description: errorMessage,
        fatal: fatal,
        ...context,
      }]);
      return;
    }
    
    // Send to Google Analytics
    window.gtag('event', 'exception', {
      description: errorMessage,
      fatal: fatal,
      ...context,
    });
    
    // Log as a regular event for easier querying in GA4
    trackEvent(EventName.ClientError, {
      error_message: errorMessage.substring(0, 500), // Truncate long messages
      error_type: typeof error === 'string' ? 'string' : error.name,
      error_stack: errorStack ? errorStack.substring(0, 500) : undefined, // Truncate long stacks
      ...context,
    });
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics] Error tracked:', { error, context, fatal });
    }
  } catch (e) {
    // Don't let analytics errors break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error tracking exception:', e);
    }
  }
}

// Set user properties for better segmentation
export function setUserProperties(user: User | null, additionalProps?: Record<string, any>) {
  try {
    if (typeof window === 'undefined') return;
    
    if (!window.gtag) {
      // Queue the user properties if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      if (!user) {
        window.dataLayer.push(['config', GA_MEASUREMENT_ID, {
          user_id: undefined
        }]);
      } else {
        window.dataLayer.push(['config', GA_MEASUREMENT_ID, {
          user_id: user.uid
        }]);
        window.dataLayer.push(['set', 'user_properties', {
          sign_up_date: user.metadata.creationTime,
          last_sign_in_date: user.metadata.lastSignInTime,
          provider: user.providerData[0]?.providerId || 'unknown',
          has_profile_photo: !!user.photoURL,
          is_email_verified: user.emailVerified,
          ...additionalProps,
        }]);
      }
      return;
    }
    
    if (!user) {
      // Clear user ID when logged out
      window.gtag('config', GA_MEASUREMENT_ID, {
        user_id: undefined
      });
      return;
    }
    
    // Set user ID for cross-platform tracking
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: user.uid
    });
    
    // Set user properties
    window.gtag('set', 'user_properties', {
      sign_up_date: user.metadata.creationTime,
      last_sign_in_date: user.metadata.lastSignInTime,
      provider: user.providerData[0]?.providerId || 'unknown',
      has_profile_photo: !!user.photoURL,
      is_email_verified: user.emailVerified,
      ...additionalProps,
    });
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics] User properties set for:', user.uid);
    }
  } catch (error) {
    // Don't let analytics errors break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error setting user properties:', error);
    }
  }
}

// Track timing metrics for performance monitoring
export function trackTiming(
  category: string,
  variable: string,
  value: number,
  label?: string
) {
  try {
    if (typeof window === 'undefined') return;
    
    if (!window.gtag) {
      // Queue the timing if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['event', 'timing_complete', {
        name: variable,
        value: Math.round(value),
        event_category: category,
        event_label: label,
      }]);
      return;
    }
    
    // Send to Google Analytics
    window.gtag('event', 'timing_complete', {
      name: variable,
      value: Math.round(value), // Round to nearest integer
      event_category: category,
      event_label: label,
    });
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Analytics] Timing: ${category}/${variable} = ${value}ms`, { label });
    }
  } catch (error) {
    // Don't let analytics errors break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error tracking timing:', error);
    }
  }
}

// Performance measurement utility with automatic timing tracking
export function measurePerformance(
  category: string,
  variable: string,
  callback: () => Promise<any> | any,
  label?: string
): Promise<any> {
  const startTime = performance.now();
  
  try {
    const result = callback();
    
    // Check if result is a promise
    if (result instanceof Promise) {
      return result.finally(() => {
        const duration = performance.now() - startTime;
        trackTiming(category, variable, duration, label);
      });
    } else {
      // Not a promise, track timing immediately
      const duration = performance.now() - startTime;
      trackTiming(category, variable, duration, label);
      return Promise.resolve(result);
    }
  } catch (error) {
    // Track error with timing
    const duration = performance.now() - startTime;
    trackTiming(category, `${variable}_error`, duration, label);
    trackError(error as Error, { category, variable, duration, label });
    throw error;
  }
}

// Track Google Ads conversions
export function trackConversion(
  conversionLabel: string,
  conversionValue?: number,
  currency = 'USD',
  transactionId?: string,
  additionalParams?: Record<string, any>
) {
  try {
    if (typeof window === 'undefined') return;
    
    const conversionParams: Record<string, any> = {
      send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
      ...additionalParams,
    };
    
    // Add value and currency if provided
    if (conversionValue !== undefined) {
      conversionParams.value = conversionValue;
      conversionParams.currency = currency;
    }
    
    // Add transaction ID if provided
    if (transactionId) {
      conversionParams.transaction_id = transactionId;
    }
    
    if (!window.gtag) {
      // Queue the conversion if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['event', 'conversion', conversionParams]);
      return;
    }
    
    // Send to Google Ads
    window.gtag('event', 'conversion', conversionParams);
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics] Google Ads Conversion:', conversionLabel, conversionParams);
    }
  } catch (error) {
    // Don't let analytics errors break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error tracking conversion:', error);
    }
  }
}

// Common conversion events for MCP Cloud
export const ConversionLabels = {
  SIGNUP: 'signup', // You'll need to get the actual label from Google Ads
  SUBSCRIPTION: 'subscription',
  SERVER_DEPLOY: 'server_deploy',
  ENTERPRISE_CONTACT: 'enterprise_contact',
} as const;

// Helper functions for common conversions
export function trackSignupConversion(user: User) {
  trackConversion(ConversionLabels.SIGNUP, undefined, 'USD', user.uid);
  
  // Also track as a regular event
  trackEvent(EventName.SignUp, {
    method: user.providerData[0]?.providerId || 'unknown',
    user_id: user.uid,
  });
}

export function trackSubscriptionConversion(
  plan: string,
  value: number,
  currency = 'USD',
  transactionId?: string
) {
  trackConversion(ConversionLabels.SUBSCRIPTION, value, currency, transactionId, {
    subscription_plan: plan,
  });
  
  // Also track as a regular event
  trackEvent(EventName.SubscriptionPurchase, {
    plan,
    value,
    currency,
    transaction_id: transactionId,
  });
}

export function trackServerDeployConversion(serverType: string, userId?: string) {
  trackConversion(ConversionLabels.SERVER_DEPLOY, undefined, 'USD', userId, {
    server_type: serverType,
  });
  
  // Also track as a regular event
  trackEvent(EventName.ServerDeploy, {
    server_type: serverType,
    user_id: userId,
  });
}

// Page view tracking for external use
export function trackPageView(path: string, title?: string) {
  try {
    if (typeof window === 'undefined') return;
    
    if (!window.gtag) {
      // Queue the page view if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['config', GA_MEASUREMENT_ID, {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
      }]);
      return;
    }
    
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Error tracking page view:', error);
    }
  }
} 
